import { type ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { IssueChat } from './IssueChat';
import { IssueLocationsPanel } from './IssueLocationsPanel';
import { BriefcaseIcon, ChevronRightIcon, ClockIcon, CloseIcon, LinkIcon, MapPinIcon, PersonIcon } from '@utils/icons';
import { typeMeta } from '../typeRegistry';
import { CriticalPathTag } from '../CriticalPathTag';
import { RelatedTaskRow } from '../RelatedTaskRow';
import type { IListItem, IRelatedTask, IRelatedTasks } from '../model/listItem';
import { formatDateRange } from '../model/formatDateRange';
import { formatIssueLocations } from '../model/issueDisplay';
import { tradeLabel } from '@utils/tradeLabel';
import { Pill } from '@components/ui/Pill/Pill';
import { ProfessionPill } from '@components/ui/ProfessionPill/ProfessionPill';
import { projectToday } from '../activitiesFilter';
import { deriveDisplayStatus } from '../model/displayStatus';
import { riskReasonKey } from '../model/risk';
import { StatusPill } from '../CardList/StatusPill';
import { IssueStatusControl } from '../IssueStatus/IssueStatusControl';
import { useCanAccess } from '@permissions/useFeatures';
import styles from './drawer.module.css';
import { useSelector } from 'react-redux';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { isImeComposing } from '@utils/ime';

export interface IActivityDrawerProps {
  item: IListItem;
  onClose: () => void;
  /** The task the issue was raised against, when the page holds it. */
  linkedTaskTitle?: string;
}

function Field({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) {
  return (
    <div className={styles.field}>
      <dt className={styles.fieldLabel}>
        <span className={styles.fieldIcon} aria-hidden="true">
          {icon}
        </span>
        {label}
      </dt>
      <dd className={styles.fieldValue}>{value}</dd>
    </div>
  );
}

function RiskRow({ label, reasons, level }: { label: string; reasons: string[]; level: number }) {
  return (
    <div className={styles.riskRow}>
      <span className={styles.riskLabel}>
        {label} · {level}/5
      </span>
      {reasons.length > 0 && <span className={styles.riskReasons}>{reasons.join(', ')}</span>}
    </div>
  );
}

interface IDepGroupProps {
  label: string;
  tasks: IRelatedTask[];
  language: string;
  year: number;
}

/** A labelled group of dependency neighbours (predecessors or successors). */
function DepGroup({ label, tasks, language, year }: IDepGroupProps) {
  return (
    <div className={styles.depGroup}>
      <span className={styles.depGroupLabel}>{label}</span>
      {tasks.map((task) => (
        <RelatedTaskRow
          key={task.id}
          task={task}
          language={language}
          year={year}
          classes={{ row: styles.depRow, name: styles.depName, meta: styles.depMeta }}
        />
      ))}
    </div>
  );
}

interface IDepsSectionProps {
  relatedTasks?: IRelatedTasks;
  language: string;
  year: number;
}

/** The drawer's Dependencies body: predecessor/successor groups, or an empty note. */
function DepsSection({ relatedTasks, language, year }: IDepsSectionProps) {
  const { t } = useTranslation();
  const predecessors = relatedTasks?.predecessors ?? [];
  const successors = relatedTasks?.successors ?? [];
  if (predecessors.length === 0 && successors.length === 0) {
    return <p className={styles.description}>{t('activities.drawer.noDependencies')}</p>;
  }
  return (
    <div className={styles.depDetail}>
      {predecessors.length > 0 && (
        <DepGroup
          label={t('activities.deps.predecessors')}
          tasks={predecessors}
          language={language}
          year={year}
        />
      )}
      {successors.length > 0 && (
        <DepGroup
          label={t('activities.deps.successors')}
          tasks={successors}
          language={language}
          year={year}
        />
      )}
    </div>
  );
}

/**
 * Renders an open list item (activity or issue) as a slide-in drawer. Its hosts
 * mount this only while an item is open — the Activities page on `?openIssue=`,
 * the dashboard's alert drawer from its own state — and unmount it on close,
 * so the panel owns the slide-in but not a deferred slide-out. Honest by
 * construction: it shows only what the IListItem model carries — no
 * checklist/audit/worker fields, which have no backing data yet.
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

export function ActivityDrawer({ item, onClose, linkedTaskTitle }: IActivityDrawerProps) {
  // Year elision in the date range is relative to the SITE's current year.
  const tz = useSelector(selectSiteTz);
  const { t, i18n } = useTranslation();
  const { siteId } = useParams<{ siteId: string }>();
  const canManageIssues = useCanAccess('issues.manage');
  const issue = item.raw.issue;
  const issueLocations = item.kind === 'issue' ? issue?.locations : undefined;
  // Narrowed bundle for the locations viewer — null when there's nothing to
  // show. Carrying the narrowed values here removes non-null assertions at the
  // render site and keeps the guard in one place.
  const locationsView =
    siteId && issueLocations && issueLocations.length > 0
      ? { siteId, locations: issueLocations }
      : null;
  // `open` drives the enter/exit animation; `mounted` keeps the panel in the
  // tree through its close animation and drops it on animationend.
  const [locationsOpen, setLocationsOpen] = useState(false);
  const [locationsMounted, setLocationsMounted] = useState(false);
  const toggleLocations = () => {
    if (locationsOpen) {
      setLocationsOpen(false);
    } else {
      setLocationsMounted(true);
      setLocationsOpen(true);
    }
  };

  // The drawer instance is reused when the URL switches between issues (the
  // call site has no `key`), so reset the companion when the underlying item
  // changes — otherwise it would leak into the next issue. Done during render
  // (React's sanctioned reset pattern) rather than in an effect, which would
  // trip the cascading-render lint.
  const [renderedItemKey, setRenderedItemKey] = useState(item.key);
  if (renderedItemKey !== item.key) {
    setRenderedItemKey(item.key);
    setLocationsOpen(false);
    setLocationsMounted(false);
  }

  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // Escape closes the companion first (innermost overlay), then the drawer —
  // unless something on top already handled it, or it cancels an IME
  // conversion (a Japanese user's Escape in a field, not a close).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || isImeComposing(e)) return;
      if (locationsOpen) {
        setLocationsOpen(false);
        return;
      }
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, locationsOpen]);

  // Scroll lock, focus-on-open, focus-return, focus-trap
  useEffect(() => {
    // Scroll lock
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Focus return: capture element that was focused before drawer opened
    const returnTarget = document.activeElement as HTMLElement | null;

    // Focus the close button (or panel as fallback) on mount
    if (closeRef.current) {
      closeRef.current.focus();
    } else {
      panelRef.current?.focus();
    }

    // Focus trap: keep Tab / Shift+Tab inside the drawer. Scoped to the overlay
    // root (not just the main panel) so the companion location panel, a sibling
    // of the panel, is reachable by keyboard when it's open.
    const onTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const root = overlayRef.current;
      if (!root) return;
      const focusable = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onTab);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onTab);
      // Return focus to previously-focused element
      returnTarget?.focus();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Every hook is above this line. The drawer only opens on an item from the
  // unified list, which stays empty until the working site resolves — so `tz`
  // is present in practice; the guard keeps that honest, as on the card.
  if (!tz) return null;

  const meta = typeMeta(item.kind);
  const typeLabel = t(meta.labelKey);
  const today = projectToday(tz);
  const year = Number(today.slice(0, 4));
  const dateRange = formatDateRange(item.start, item.end, i18n.language, year);

  return createPortal(
    <div
      ref={overlayRef}
      className={`${styles.overlay} ${styles.overlayOpen}`}
      role="presentation"
      onClick={onClose}
    >
      {locationsView && locationsMounted && (
        <IssueLocationsPanel
          open={locationsOpen}
          siteId={locationsView.siteId}
          locations={locationsView.locations}
          onClose={() => setLocationsOpen(false)}
          onExited={() => setLocationsMounted(false)}
        />
      )}
      <aside
        ref={panelRef}
        className={`${styles.panel} ${styles.panelOpen}`}
        role="dialog"
        aria-modal="true"
        aria-label={item.title}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={closeRef}
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label={t('activities.drawer.close')}
        >
          <CloseIcon size={18} />
        </button>

        <div className={styles.head}>
          <span className={styles.id}>{typeLabel}</span>
          <div className={styles.titleLine}>
            <h2 className={styles.title}>{item.title}</h2>
            {item.isOnCriticalPath && <CriticalPathTag />}
          </div>
          <div className={styles.pills}>
            {/* The card's status chip — same words, same colour. */}
            <StatusPill status={deriveDisplayStatus(item, today)} />
            {item.isCritical && <Pill className={styles.metaTone}>{t('activities.filter.critical')}</Pill>}
            {item.isSafety && <Pill className={styles.metaTone}>{t('activities.filter.safety')}</Pill>}
            {item.kind === 'issue' && item.raw.issue?.isPrivate && (
              <Pill className={styles.metaTone}>{t('activities.drawer.private')}</Pill>
            )}
          </div>
          {/* The same status control as the card's, from the same table. */}
          {item.kind === 'issue' && issue && siteId && (
            <IssueStatusControl
              issue={issue}
              siteId={siteId}
              canManage={canManageIssues}
              className={styles.statusAction}
            />
          )}
        </div>

        <div className={styles.divider} />

        <dl className={styles.grid}>
          {item.kind === 'activity' ? (
            <>
              {dateRange && (
                <Field
                  icon={<ClockIcon size={15} />}
                  label={t('activities.drawer.schedule')}
                  value={dateRange}
                />
              )}
              {item.profession && (
                <Field
                  icon={<BriefcaseIcon size={15} />}
                  label={t('activities.drawer.assignee')}
                  value={<ProfessionPill profession={item.profession} showContractor />}
                />
              )}
              {typeof item.progressPercent === 'number' && (
                <Field
                  icon={<ClockIcon size={15} />}
                  label={t('activities.drawer.progress')}
                  value={`${item.progressPercent}%`}
                />
              )}
            </>
          ) : (
            <>
              {item.end && (() => {
                const dueDateLabel = formatDateRange(undefined, item.end, i18n.language, year);
                return dueDateLabel ? (
                  <Field
                    icon={<ClockIcon size={15} />}
                    label={t('activities.drawer.due')}
                    value={dueDateLabel}
                  />
                ) : null;
              })()}
              {item.assigneeName && (
                <Field
                  icon={<PersonIcon size={15} />}
                  label={t('activities.drawer.assignee')}
                  value={item.assigneeName}
                />
              )}
              {(() => {
                // The issue's minimized profession only carries tradeId — derive the
                // name from tradeLabel (same as the issue dialog / the card).
                const tradeId = item.raw.issue?.profession?.tradeId;
                const profName = tradeId ? tradeLabel(t, tradeId) : '';
                return profName ? (
                  <Field
                    icon={<BriefcaseIcon size={15} />}
                    label={t('activities.drawer.trade')}
                    value={profName}
                  />
                ) : null;
              })()}
              {linkedTaskTitle && (
                <Field icon={<LinkIcon size={15} />} label={t('activities.drawer.task')} value={linkedTaskTitle} />
              )}
              {issueLocations && issueLocations.length > 0 && (() => {
                const locLabel = formatIssueLocations(issueLocations);
                return locLabel ? (
                  <Field
                    icon={<MapPinIcon size={15} />}
                    label={t('activities.drawer.location')}
                    value={locLabel}
                  />
                ) : null;
              })()}
            </>
          )}
        </dl>

        {locationsView && (
          <button
            type="button"
            className={`${styles.locToggle} ${locationsOpen ? styles.locToggleActive : ''}`}
            aria-expanded={locationsOpen}
            onClick={toggleLocations}
          >
            <MapPinIcon size={13} />
            {t('activities.drawer.viewLocations')}
            <span
              className={`${styles.locChevron} ${locationsOpen ? styles.locChevronOpen : ''}`}
              aria-hidden="true"
            >
              <ChevronRightIcon size={14} />
            </span>
          </button>
        )}

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('activities.drawer.risk')}</h3>
          <div className={styles.riskDetail}>
            <RiskRow
              label={t('activities.drawer.onTimeRisk')}
              reasons={item.onTimeRisk.reasons.map((c) => t(riskReasonKey(c)))}
              level={item.onTimeRisk.level}
            />
            <RiskRow
              label={t('activities.drawer.safetyRisk')}
              reasons={item.safety.reasons.map((c) => t(riskReasonKey(c)))}
              level={item.safety.level}
            />
          </div>
        </section>

        {item.kind === 'activity' && (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>{t('activities.drawer.dependencies')}</h3>
            <DepsSection relatedTasks={item.relatedTasks} language={i18n.language} year={year} />
          </section>
        )}

        {item.kind === 'issue' && issue && siteId && <IssueChat siteId={siteId} issue={issue} />}
      </aside>
    </div>,
    document.body,
  );
}
